import React, { useEffect } from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { TextHeadline1, TextSubHeading, TextCallout2, IconClose, Link } from 'blocket-ui'
import { mediaQueries } from '@blazar-plugins/device'
import { PackageFilter, PackageLocation, PackageHero } from './package.svg.js'

const Wrapper = styled.div`
  height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: translateY(100%);
  transition: 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-property: transform, z-index;
  z-index: -100;
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  * {
    box-sizing: border-box;
  }

  @media ${mediaQueries.medium} {
    transition: initial;
    transform: translateY(0);
    background: transparent;
    justify-content: center;
    width: 0;
    &.active {
      width: auto;
    }
  }

  &.active {
    transform: translateY(0);
    z-index: 10000;
  }
`

const PageWrapper = styled.div`
  padding: 30px 16px;
  display: flex;
  flex-direction: column;
  > *:first-child {
    margin-top: 0;
  }
  > * {
    flex: 1 1 auto;
    @media ${mediaQueries.medium} {
      flex: 1 0 0;
    }
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-top: 27px;
  }
  > *:not(ul) {
    text-align: center;
  }
`

const Hero = styled.div`
  background: #d8e6f3;
  flex: 0 0 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    margin-left: 31px;
  }
`

const Title = styled(TextHeadline1)`
  color: rgb(54, 52, 51);
  font-size: 24px;
  line-height: 28px;
  text-align: center;
  margin-bottom: 10px;
`

const SubTitle = styled(TextSubHeading)`
  line-height: 26px;
`

const InfoText = styled(TextCallout2)`
  color: rgb(54, 52, 51);
  &,
  a {
    font-size: 15px;
    line-height: 20px;
  }
  strong {
    font-family: BlocketSans-Bold;
  }
`

const PageContainer = styled.div``

const List = styled.ul`
  li + li {
    margin-top: 25px;
  }
`

const ListItem = styled.li`
  display: flex;
  align-items: center;
  text-align: left;
  > svg {
    flex: 0 0 56px;
  }
`

const ListItemContent = styled.div`
  margin-left: 20px;
`

const Header = styled.div`
  display: flex;
  padding: 10px 16px;
  justify-content: flex-end;
  width: 100%;
  svg {
    width: 32px;
    flex: 0 0 32px;
    margin-right: -6px;
    cursor: pointer;
  }
  @media ${mediaQueries.medium} {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    background: transparent;
  }
`

const Inner = styled.div`
  width: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  height: 100%;
  -ms-overflow-style: none;
  overflow: auto;
  @media ${mediaQueries.medium} {
    position: relative;
    max-width: 420px;
    min-height: 60vh;
    height: auto;
    margin: 0 auto;
    border-radius: 6px;
    z-index: 10000;
    border: 1px solid rgb(225, 225, 225);
    box-shadow: 0px 2px 2px 2px rgba(0, 0, 0, 0.06);
  }
`

const Overlay = styled.div`
  display: none;
  background: rgba(255, 255, 255, 0.7);
  z-index: -100;
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 0;
  @media ${mediaQueries.medium} {
    &.active {
      height: 100%;
      z-index: 9999;
      display: block;
    }
  }
`

export const OnBoardingModal = ({ onClose, active }) => {
  useEffect(() => {
    if (active) {
      window.onscroll = () => {
        window.scrollTo(0, 0)
      }
    } else {
      window.onscroll = () => {}
    }
  }, [active])

  return (
    <>
      <Wrapper className={active && 'active'}>
        <Inner>
          <Header>
            <IconClose onClick={onClose} />
          </Header>
          <Hero>
            <PackageHero />
          </Hero>
          <PageWrapper>
            <PageContainer>
              <Title>Köp prylar från Hela Sverige!</Title>
              <InfoText>
                Nu är det enkelt att hitta alla annonser som erbjuder frakt och säker betalning med{' '}
                <Link to="https://www.blocket.se/trygg-frakt-och-betalning-med-blocketpaketet">
                  Blocketpaketet
                </Link>
                .
              </InfoText>
            </PageContainer>
            <PageContainer>
              <List>
                <ListItem>
                  <PackageFilter />
                  <ListItemContent>
                    <SubTitle>Använd filtret</SubTitle>
                    <InfoText>
                      Då får du se alla annonser där säljaren erbjuder frakt och säker betalning.
                    </InfoText>
                  </ListItemContent>
                </ListItem>
                <ListItem>
                  <PackageLocation />
                  <ListItemContent>
                    <SubTitle>Välj Hela Sverige</SubTitle>
                    <InfoText>
                      Glöm inte att välja <strong>Hela Sverige</strong> i filtret för att se allt
                      som kan skickas.
                    </InfoText>
                  </ListItemContent>
                </ListItem>
              </List>
            </PageContainer>
          </PageWrapper>
        </Inner>
        <Overlay className={active && 'active'} onClick={onClose} />
      </Wrapper>
    </>
  )
}

OnBoardingModal.propTypes = {
  active: PropTypes.bool,
  onClose: PropTypes.func,
}

export default OnBoardingModal
